import _extends from "@babel/runtime-corejs3/helpers/extends";
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";
import _JSON$stringify from "@babel/runtime-corejs3/core-js-stable/json/stringify";
import _reduceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/reduce";
import _Object$assign from "@babel/runtime-corejs3/core-js-stable/object/assign";
import * as _ from "../../../util/lodash";
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Table as UITable } from 'semantic-ui-react';
import { getContext } from 'react-recompose';
import { toUIProps } from "../../../util";
import { positions, selectionTypes, textAlignments, sizes } from "../../../enums";
import { whitelistStyles } from "../hoc";
import { getNewSelection, getTableProps, showWarnings, rowValuesToIndexes } from "./utils";
var UNSUPPORTED_PROPS = ['renderBodyRow', 'headerRow', 'footerRow', 'tableData', 'color', 'basic', 'padded', 'stackable', 'unstackable', 'celled'];
var ADDED_PROPS = ['allowScroll', 'isCompact', 'isInverted', 'isStriped', 'isStructured', 'isTransparent', 'onSelection', 'selectionType', 'selectedRows', 'showHorizontalDivider', 'showOuterBorder', 'showVerticalDivider'];
var MODIFIED_PROPS = [];
var COPIED_PROPS = ['Header', 'HeaderCell', 'Body', 'Row', 'Cell', 'Footer'];
/**
 * Renders semantic-ui table with added styles, props and logic for selecting row(s).
 */

var PrivateTable = function PrivateTable(p) {
  var _classnames;

  var _useState = useState([]),
      selectedIndexes = _useState[0],
      setSelectedIndexes = _useState[1]; //ex. [4, 5, 10]
  //break up passed in children into Table related components, so we can hook up our selection related onClick handlers to rows and then rebuild the table


  var _useMemo = useMemo(function () {
    return getTableProps(p.children);
  }, [p.children]),
      tableHeaderProps = _useMemo.tableHeaderProps,
      tableBodyProps = _useMemo.tableBodyProps,
      tableFooterProps = _useMemo.tableFooterProps,
      rows = _useMemo.rows;

  var rowValues = useMemo(function () {
    return _mapInstanceProperty(_).call(_, rows, function (row) {
      return row.props.value;
    });
  }, [rows]); //user can change the selected values from outside, for example, he might remove items and in that case the selected indexes need to change. Or he could reload a new set of items and might want to clear the selected rows

  useEffect(function () {
    setSelectedIndexes(rowValuesToIndexes(p.selectionType, p.selectedRows, rowValues));
  }, [p.selectionType, _JSON$stringify(p.selectedRows), rowValues]); // handles click event setting selected row

  var handleRowClick = function handleRowClick(e, targetIndex, row) {
    e.persist();
    var newSelection = getNewSelection(e, p.selectionType, selectedIndexes, targetIndex, rows.length);
    setSelectedIndexes(newSelection); //in case the dev has put his own onClick onto the row, make sure that's also being called

    if (_.isFunction(row.props.onClick)) {
      row.props.onClick(e, {
        index: targetIndex,
        value: row.props.value
      });
    } //calling the onSelection callback and passing the selected indexes/values


    if (_.isFunction(p.onSelection)) {
      var data = _mapInstanceProperty(_).call(_, newSelection, function (index) {
        return {
          index: index,
          value: rowValues[index]
        };
      });

      p.onSelection(e, data);
    }
  }; //building out a list of props to pass down to the semantic ui table component


  var props = _extends({}, p, {
    inverted: p.isInverted,
    basic: false,
    striped: p.isStriped,
    compact: p.isCompact,
    structured: p.isStructured
  });

  var isSelectable = p.selectionType === selectionTypes.SINGLE || p.selectionType === selectionTypes.MULTIPLE;
  var UIProps = toUIProps({
    props: props,
    UNSUPPORTED_PROPS: UNSUPPORTED_PROPS,
    ADDED_PROPS: ADDED_PROPS,
    MODIFIED_PROPS: MODIFIED_PROPS
  }); //clear up unsupported props(in case the dev is trying to pass down more than we support)

  UIProps.className = classnames((_classnames = {
    //build out the list of class names we want to add to our component
    scrollable: p.allowScroll && !p.isTransparent
  }, _classnames["font-size--" + p.fontSize] = true, _classnames.bordered = p.showOuterBorder || p.showVerticalDivider || p.showHorizontalDivider, _classnames['bordered--outer'] = p.showOuterBorder, _classnames['bordered--vertically'] = p.showVerticalDivider, _classnames['bordered--horizontally'] = p.showHorizontalDivider, _classnames.selectable = isSelectable, _classnames.transparent = p.isTransparent, _classnames)); //turns the array of indexes into an object, to avoid searching when trying to set 'active' rows

  var selectedIndexesObj = isSelectable ? _reduceInstanceProperty(selectedIndexes).call(selectedIndexes, function (acc, curr) {
    acc[curr] = true;
    return acc;
  }, {}) : {}; //above code will turn the [4, 5, 10] selected indexes into an object that can easily tell if a certain index is selected or not(without needing to do a find on the array for each individual row)
  // {
  //   '4': true,
  //   '5': true,
  //   '10': true,
  // }

  showWarnings(p);
  return /*#__PURE__*/React.createElement("div", {
    className: classnames('ck-table', p.className, {
      'ck-table--scroll': p.allowScroll
    })
  }, /*#__PURE__*/React.createElement(UITable, UIProps, tableHeaderProps ? /*#__PURE__*/React.createElement(UITable.Header, tableHeaderProps) : null, /*#__PURE__*/React.createElement(UITable.Body, tableBodyProps, _mapInstanceProperty(rows).call(rows, function (row, rowIndex) {
    return /*#__PURE__*/React.createElement(UITable.Row, _extends({
      key: "row-" + rowIndex
    }, row.props, isSelectable && {
      //conditionally override the rows active prop and onClick handlers
      active: !!selectedIndexesObj[rowIndex],
      onClick: function onClick(e) {
        return handleRowClick(e, rowIndex, row);
      }
    }));
  }, this)), tableFooterProps ? /*#__PURE__*/React.createElement(UITable.Footer, tableFooterProps) : null));
};

PrivateTable.defaultProps = {
  allowScroll: false,
  fontSize: sizes.S,
  showOuterBorder: true,
  showVerticalDivider: true,
  showHorizontalDivider: true,
  isCompact: false,
  isInverted: false,
  isStriped: false,
  isStructured: false,
  isTransparent: false,
  onSelection: undefined,
  selectionType: selectionTypes.NONE,
  selectedRows: [],
  textAlign: textAlignments.LEFT,
  verticalAlign: positions.verticalAlign.MIDDLE
};
PrivateTable.propTypes = {
  allowScroll: PropTypes.bool,
  fontSize: PropTypes.oneOf(sizes.S_M_L),
  showOuterBorder: PropTypes.bool,
  showVerticalDivider: PropTypes.bool,
  showHorizontalDivider: PropTypes.bool,
  isCompact: PropTypes.bool,
  isInverted: PropTypes.bool,
  isStriped: PropTypes.bool,
  isStructured: PropTypes.bool,
  isTransparent: PropTypes.bool,
  onSelection: PropTypes.func,
  selectionType: PropTypes.oneOf(selectionTypes.ALL),
  selectedRows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  textAlign: PropTypes.oneOf(textAlignments.ALL),
  verticalAlign: PropTypes.oneOf(positions.verticalAlign.ALL)
}; //whitelisting styles and adding context.

var Table = whitelistStyles(['width'])(getContext({
  isInverted: PropTypes.bool
})(PrivateTable)); //also adding UITable components that can be used with our PrivateTable. It's important to copy the props after the whitelistStyles happens, otherwise the added props are going to be removed!!!

_Object$assign(Table, _.pick(UITable, COPIED_PROPS));

export default Table;
//# sourceMappingURL=index.js.map